// Single source of truth for the Settings menu structure.
//
// The Settings dropdown used to be a flat list of 11 items. It's now grouped:
// three groups (each opens its own landing page of cards) plus two standalone
// items. The leaf ids below MUST stay equal to their `/settings/{id}` route
// segment and to the panelCatalog `teamlioPath` (cross-app contract with
// Messenger) — only the *grouping* is new, never the leaf ids/paths.

import {
  Briefcase,
  MapPin,
  Building2,
  UserCircle,
  Settings,
  BookOpen,
  Megaphone,
  Database,
  Hash,
  Lock,
  Shield,
  Wallet,
  FilePenLine,
  SlidersHorizontal,
  Mail,
  BellRing,
  Tablet,
  type LucideIcon,
} from 'lucide-react';
import { BLOG_MANAGEMENT_HEAD_OFFICE_ID } from './panelCatalog';
import { sessionGet } from '../utils/sessionStore';

export type SettingsTab =
  | 'company'
  | 'office'
  | 'department'
  | 'designation'
  | 'terms-and-conditions'
  | 'company-policy'
  | 'policy-rules'
  | 'startupkhata'
  | 'task-code'
  | 'access-management'
  | 'roles'
  | 'announcements'
  | 'blog-management'
  | 'salary-compliance'
  | 'smtp-settings'
  | 'notifications'
  | 'attendance-kiosk';

export type SettingsGroupId = 'organization' | 'policies' | 'access';

export interface SettingsLeaf {
  id: SettingsTab;
  label: string;
  description: string;
  icon: LucideIcon;
  roles: string[];
  // When true, this leaf is visible to EVERY role and regardless of any Access
  // profile (read-for-all pages like Company Policy & Office). The page itself
  // still gates who can edit/add — visibility here never grants write rights.
  publicView?: boolean;
}

// Leaf definitions. `roles` is the fallback gate used only when the user has no
// Access profile (see isLeafVisible); identical to the old per-item role lists.
export const SETTINGS_LEAVES: Record<SettingsTab, SettingsLeaf> = {
  company: {
    id: 'company',
    label: 'Company',
    description: 'Profile & details',
    icon: Briefcase,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  office: {
    id: 'office',
    label: 'Office',
    description: 'Locations & addresses',
    icon: MapPin,
    roles: ['admin', 'founder', 'director', 'employee', 'hr head', 'hr admin', 'manager', 'supervisor'],
    publicView: true,
  },
  department: {
    id: 'department',
    label: 'Department',
    description: 'Departments & teams',
    icon: Building2,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  designation: {
    id: 'designation',
    label: 'Designation',
    description: 'Job titles & roles',
    icon: UserCircle,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  'terms-and-conditions': {
    id: 'terms-and-conditions',
    label: 'Terms & Conditions',
    description: 'Employment terms',
    icon: Settings,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  'company-policy': {
    id: 'company-policy',
    label: 'Company Policy',
    description: 'Policies employees read',
    icon: BookOpen,
    roles: ['admin', 'founder', 'director', 'employee', 'hr head', 'hr admin', 'manager', 'supervisor'],
    publicView: true,
  },
  announcements: {
    id: 'announcements',
    label: 'Announcements',
    description: 'Company-wide notices',
    icon: Megaphone,
    roles: ['admin'],
  },
  'blog-management': {
    id: 'blog-management',
    label: 'Blog Management',
    description: 'Draft and publish public articles',
    icon: FilePenLine,
    roles: ['admin'],
  },
  'policy-rules': {
    id: 'policy-rules',
    label: 'Policy Rules',
    description: 'Attendance, late-coming, overtime & leave rules',
    icon: SlidersHorizontal,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin'],
  },
  startupkhata: {
    id: 'startupkhata',
    label: 'Startup Khata',
    description: 'Integration & sync',
    icon: Database,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  'task-code': {
    id: 'task-code',
    label: 'Task Settings',
    description: 'Task IDs & notifications',
    icon: Hash,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  'access-management': {
    id: 'access-management',
    label: 'Access Management',
    description: 'Per-profile page access',
    icon: Lock,
    // 'manager' is here for the Lio toggle only: the grid renders read-only for
    // managers except the "AI Assistant (Lio)" row, and the hrms backend narrows
    // their saves to that cell regardless (routers/access.py).
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin', 'manager'],
  },
  roles: {
    id: 'roles',
    label: 'Roles',
    description: 'Roles & default access',
    icon: Shield,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin'],
  },
  notifications: {
    id: 'notifications',
    label: 'Notifications',
    description: 'Who gets told about what',
    icon: BellRing,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin'],
  },
  'salary-compliance': {
    id: 'salary-compliance',
    label: 'Salary & Compliance',
    description: 'Retainer-ship & payroll policy',
    icon: Wallet,
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin'],
  },
  'attendance-kiosk': {
    id: 'attendance-kiosk',
    label: 'Attendance Kiosk',
    description: 'Shared device sign-in for marking attendance',
    icon: Tablet,
    // Same tier as the other credential-minting pages: creating a kiosk hands
    // out one password that can punch for a whole roster. Managers are not in
    // the fallback list; grant them `set-attendance-kiosk` in Access
    // Management if a branch wants them running their own gate device.
    roles: ['admin', 'founder', 'director', 'hr head', 'hr admin'],
  },
  'smtp-settings': {
    id: 'smtp-settings',
    label: 'Email (SMTP)',
    description: 'Send email via your own mail server',
    icon: Mail,
    // Admin only, deliberately narrower than every other settings leaf: this
    // page holds the branch's outgoing-mail password. `roles` is only the
    // fallback for accounts with no Access profile (admins have no Employee
    // row); anyone else needs `set-smtp-settings` granted in Access Management.
    roles: ['admin'],
  },
};

export interface SettingsGroup {
  kind: 'group';
  id: SettingsGroupId;
  label: string;
  description: string;
  icon: LucideIcon;
  children: SettingsTab[];
}

export interface SettingsNavLeaf {
  kind: 'item';
  id: SettingsTab;
}

export type SettingsNavEntry = SettingsGroup | SettingsNavLeaf;

// Ordered top-level menu: 3 groups + 2 standalone items.
export const SETTINGS_NAV: SettingsNavEntry[] = [
  {
    kind: 'group',
    id: 'organization',
    label: 'Organization',
    description: 'Company structure & foundational records',
    icon: Building2,
    children: ['company', 'office', 'department', 'designation', 'attendance-kiosk', 'salary-compliance', 'smtp-settings'],
  },
  {
    kind: 'group',
    id: 'policies',
    label: 'Policies & Notices',
    description: 'Terms, policies & company announcements',
    icon: BookOpen,
    children: ['terms-and-conditions', 'company-policy', 'policy-rules', 'notifications', 'announcements', 'blog-management'],
  },
  {
    kind: 'group',
    id: 'access',
    label: 'Access Control',
    description: 'Permissions, profiles & roles',
    icon: Lock,
    children: ['access-management', 'roles'],
  },
  { kind: 'item', id: 'startupkhata' },
  { kind: 'item', id: 'task-code' },
];

export const SETTINGS_GROUPS: Record<SettingsGroupId, SettingsGroup> = SETTINGS_NAV.reduce(
  (acc, entry) => {
    if (entry.kind === 'group') acc[entry.id] = entry;
    return acc;
  },
  {} as Record<SettingsGroupId, SettingsGroup>
);

export function isSettingsGroupId(value: string | undefined): value is SettingsGroupId {
  return value === 'organization' || value === 'policies' || value === 'access';
}

// Parent group of a leaf tab, if any (used for the back-link breadcrumb).
export function groupForChild(tab: string | undefined): SettingsGroup | undefined {
  return SETTINGS_NAV.find(
    (e): e is SettingsGroup => e.kind === 'group' && e.children.includes(tab as SettingsTab)
  );
}

export interface AccessCtx {
  hasAccess: boolean;
  isPathVisible: (pathname: string) => boolean;
  role: string;
}

// Same gate as before: when the user has an Access profile, the profile is
// authoritative (per-path); otherwise fall back to role.
export function isLeafVisible(leaf: SettingsLeaf, ctx: AccessCtx): boolean {
  if (leaf.id === 'blog-management' && sessionGet('headOfficeID') !== BLOG_MANAGEMENT_HEAD_OFFICE_ID) return false;
  // Read-for-all pages are visible to everyone, ignoring both the role list and
  // any Access profile. Write access is still enforced inside the page itself.
  if (leaf.publicView) return true;
  // Intern is the employee tier — same settings visibility as employee.
  const role = (ctx.role || "").toLowerCase() === "intern" ? "employee" : ctx.role;
  if (leaf.id === 'blog-management' && role === 'admin') return true;
  return ctx.hasAccess ? ctx.isPathVisible(`/settings/${leaf.id}`) : leaf.roles.includes(role);
}

export function visibleChildren(group: SettingsGroup, ctx: AccessCtx): SettingsLeaf[] {
  return group.children.map((id) => SETTINGS_LEAVES[id]).filter((leaf) => isLeafVisible(leaf, ctx));
}

// A group shows iff at least one of its children is visible; a standalone item
// shows by its own leaf rule.
export function isEntryVisible(entry: SettingsNavEntry, ctx: AccessCtx): boolean {
  if (entry.kind === 'group') return visibleChildren(entry, ctx).length > 0;
  return isLeafVisible(SETTINGS_LEAVES[entry.id], ctx);
}
